import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw,
  Inbox,
  PackageCheck,
  PackageOpen,
  ArrowDownToLine,
  CheckCircle2,
  ArrowRightLeft,
  Package,
  FileText,
} from "lucide-react";
import PickListDialog from "@/pages/stock/PickListDialog";

const PRIORITY_STYLE = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
  low: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function MyTasksView({ shops = [], onJumpToQueue }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [shopFilter, setShopFilter] = useState("all");
  const [pickListShopId, setPickListShopId] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (shopFilter !== "all") params.shop_id = shopFilter;
      const { data } = await api.get("/management/my-tasks", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [shopFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const setItemStatus = async (id, status) => {
    try {
      await api.put(`/management/restock-queue/${id}`, { status });
      toast.success(`Marked ${status}`);
      fetchTasks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 6 — receive (actually moves stock between shops)
  const [receiveItem, setReceiveItem] = useState(null);
  const receiveFull = async (item) => {
    if (
      !window.confirm(
        `Receive ${item.quantity_requested} units of "${item.product_snapshot?.product_name}" in full?\n\n` +
          `This will move stock from source shop(s) → ${item.destination_shop_name} (FEFO).`
      )
    )
      return;
    try {
      await api.post(`/management/restock-queue/${item.id}/receive`, {});
      toast.success("Received & stock moved");
      fetchTasks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const receivePartial = async (item, perReservation) => {
    try {
      await api.post(`/management/restock-queue/${item.id}/receive`, {
        reservations: perReservation,
      });
      toast.success("Partial receive saved");
      setReceiveItem(null);
      fetchTasks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const counts = data?.counts || {
    pending_requests: 0,
    awaiting_dispatch: 0,
    incoming_transfers: 0,
    total: 0,
  };

  // Collect unique source-shop ids from awaiting_dispatch reservations
  const dispatchSourceShops = useMemo(() => {
    const map = new Map();
    for (const it of data?.awaiting_dispatch || []) {
      for (const r of it.my_reservations || []) {
        if (!map.has(r.source_shop_id)) {
          map.set(r.source_shop_id, r.source_shop_name);
        }
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const openPickList = () => {
    if (dispatchSourceShops.length === 0) {
      toast.error("No source shops to pick from");
      return;
    }
    if (dispatchSourceShops.length === 1) {
      setPickListShopId(dispatchSourceShops[0].id);
    } else {
      const choice = window.prompt(
        "Multiple source shops have reservations. Enter the shop name to generate a pick list:\n\n" +
          dispatchSourceShops.map((s) => "• " + s.name).join("\n")
      );
      if (!choice) return;
      const match = dispatchSourceShops.find(
        (s) => s.name.toLowerCase() === choice.trim().toLowerCase()
      );
      if (!match) {
        toast.error("No matching shop");
        return;
      }
      setPickListShopId(match.id);
    }
  };

  return (
    <div className="space-y-4" data-testid="my-tasks-view">
      {/* Header & filter */}
      <Card className="p-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Your action queue
            </div>
            <div className="text-sm">
              <span className="font-mono font-semibold text-[#9E6D5B]">
                {counts.total}
              </span>{" "}
              open task{counts.total === 1 ? "" : "s"} across your shop(s).
            </div>
          </div>
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger className="h-9 w-full sm:w-48" data-testid="mt-shop-filter">
              <SelectValue placeholder="All my shops" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All my shops</SelectItem>
              {shops.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTasks}
            className="h-9"
            data-testid="mt-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Three sections */}
      <Section
        title="Pending Requests"
        subtitle="Restock requests awaiting reservation or progress at your shop(s)"
        icon={<Inbox className="w-5 h-5 text-amber-600" />}
        accent="amber"
        count={counts.pending_requests}
        items={data?.pending_requests || []}
        emptyText="No pending requests."
        testid="mt-pending"
        renderRow={(it) => (
          <PendingRow
            key={it.id}
            item={it}
            onJump={() => onJumpToQueue && onJumpToQueue(it.id)}
          />
        )}
      />

      <Section
        title="Awaiting Dispatch"
        subtitle="Stock you have reserved that needs to be physically sent out"
        icon={<PackageOpen className="w-5 h-5 text-blue-600" />}
        accent="blue"
        count={counts.awaiting_dispatch}
        items={data?.awaiting_dispatch || []}
        emptyText="No reservations awaiting dispatch."
        testid="mt-dispatch"
        headerActions={
          counts.awaiting_dispatch > 0 ? (
            <Button
              size="sm"
              variant="outline"
              onClick={openPickList}
              className="h-7 text-xs"
              data-testid="mt-open-pick-list"
            >
              <FileText className="w-3.5 h-3.5 mr-1" />
              Generate Pick List
            </Button>
          ) : null
        }
        renderRow={(it) => (
          <DispatchRow
            key={it.id}
            item={it}
            onMarkDispatched={() => setItemStatus(it.id, "dispatched")}
          />
        )}
      />

      <Section
        title="Incoming Transfers"
        subtitle="Stock dispatched towards your shop — confirm on arrival"
        icon={<ArrowDownToLine className="w-5 h-5 text-emerald-600" />}
        accent="emerald"
        count={counts.incoming_transfers}
        items={data?.incoming_transfers || []}
        emptyText="No incoming transfers."
        testid="mt-incoming"
        renderRow={(it) => (
          <IncomingRow
            key={it.id}
            item={it}
            onReceiveFull={() => receiveFull(it)}
            onReceivePartial={() => setReceiveItem(it)}
          />
        )}
      />

      <PickListDialog
        open={!!pickListShopId}
        onOpenChange={(o) => !o && setPickListShopId(null)}
        sourceShopId={pickListShopId}
        onDispatched={fetchTasks}
      />

      <ReceivePartialDialog
        item={receiveItem}
        onCancel={() => setReceiveItem(null)}
        onConfirm={(perReservation) => receivePartial(receiveItem, perReservation)}
      />
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon,
  accent,
  count,
  items,
  renderRow,
  emptyText,
  testid,
  headerActions,
}) {
  const accents = {
    amber: "border-l-amber-500",
    blue: "border-l-blue-500",
    emerald: "border-l-emerald-500",
  };
  return (
    <Card className={`p-0 border-l-4 ${accents[accent] || "border-l-slate-300"}`} data-testid={testid}>
      <div className="p-3 flex items-center justify-between border-b">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="font-semibold text-sm">{title}</div>
            <div className="text-[11px] text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          <Badge variant="outline" className="font-mono text-sm" data-testid={`${testid}-count`}>
            {count}
          </Badge>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-xs text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-1 opacity-30" />
          {emptyText}
        </div>
      ) : (
        <div className="divide-y">{items.map(renderRow)}</div>
      )}
    </Card>
  );
}

function PriorityChip({ priority }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${
        PRIORITY_STYLE[priority] || PRIORITY_STYLE.normal
      }`}
    >
      {priority}
    </span>
  );
}

function ProductCell({ item }) {
  const p = item.product_snapshot || {};
  return (
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate">{p.product_name || "—"}</div>
      <div className="text-[11px] text-muted-foreground truncate">
        {p.brand_name || "—"} · {p.auto_barcode || ""}
      </div>
    </div>
  );
}

function PendingRow({ item, onJump }) {
  const activeRes = (item.reservations || []).filter((r) => !r.released_at);
  const reservedQty = activeRes.reduce((s, r) => s + Number(r.quantity || 0), 0);
  const remaining = Math.max(
    0,
    Number(item.quantity_requested) -
      Number(item.quantity_fulfilled || 0) -
      reservedQty
  );
  return (
    <div className="p-3 flex items-center gap-3" data-testid={`mt-pending-row-${item.id}`}>
      <PriorityChip priority={item.priority} />
      <ProductCell item={item} />
      <div className="text-right text-xs">
        <div className="font-mono font-semibold">
          {item.quantity_requested} req
        </div>
        <div className="text-muted-foreground">
          {reservedQty} reserved · {remaining} open
        </div>
      </div>
      <Badge variant="outline">{item.destination_shop_name}</Badge>
      <Button
        size="sm"
        variant="outline"
        onClick={onJump}
        className="h-7 text-xs"
        data-testid={`mt-pending-open-${item.id}`}
      >
        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
        Open
      </Button>
    </div>
  );
}

function DispatchRow({ item, onMarkDispatched }) {
  return (
    <div className="p-3 flex items-center gap-3" data-testid={`mt-dispatch-row-${item.id}`}>
      <PriorityChip priority={item.priority} />
      <ProductCell item={item} />
      <div className="text-xs text-right">
        <div className="font-mono font-semibold text-blue-700">
          ×{item.my_outbound_qty} to send
        </div>
        <div className="text-muted-foreground">
          {(item.my_reservations || [])
            .map((r) => `${r.source_shop_name} ×${r.quantity}`)
            .join(" · ")}
        </div>
      </div>
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
        → {item.destination_shop_name}
      </Badge>
      <Button
        size="sm"
        onClick={onMarkDispatched}
        className="h-7 text-xs text-white"
        style={{ backgroundColor: "#9E6D5B" }}
        data-testid={`mt-mark-dispatched-${item.id}`}
      >
        <PackageCheck className="w-3.5 h-3.5 mr-1" />
        Mark Dispatched
      </Button>
    </div>
  );
}

function IncomingRow({ item, onReceiveFull, onReceivePartial }) {
  const activeRes = (item.reservations || []).filter(
    (r) => !r.released_at && !r.received_at
  );
  const expected = activeRes.reduce((s, r) => s + Number(r.quantity || 0), 0);
  return (
    <div className="p-3 flex items-center gap-3" data-testid={`mt-incoming-row-${item.id}`}>
      <PriorityChip priority={item.priority} />
      <ProductCell item={item} />
      <div className="text-xs text-right">
        <div className="font-mono font-semibold text-emerald-700">
          ×{expected} arriving
        </div>
        <div className="text-muted-foreground">
          {(activeRes || [])
            .map((r) => `${r.source_shop_name} ×${r.quantity}`)
            .join(" · ")}
        </div>
      </div>
      <Badge variant="outline">{item.destination_shop_name}</Badge>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={onReceivePartial}
          className="h-7 text-xs"
          data-testid={`mt-mark-partial-${item.id}`}
          disabled={activeRes.length === 0}
          title="Enter actual qty received per reservation"
        >
          Partial
        </Button>
        <Button
          size="sm"
          onClick={onReceiveFull}
          className="h-7 text-xs text-white"
          style={{ backgroundColor: "#10b981" }}
          data-testid={`mt-mark-received-${item.id}`}
          disabled={activeRes.length === 0}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
          Receive Full
        </Button>
      </div>
    </div>
  );
}

// Phase 6 — Receive Partial dialog
function ReceivePartialDialog({ item, onCancel, onConfirm }) {
  const [perRes, setPerRes] = useState({}); // { reservation_id: qty (string) }

  useEffect(() => {
    if (!item) return;
    const active = (item.reservations || []).filter(
      (r) => !r.released_at && !r.received_at
    );
    const seed = {};
    for (const r of active) seed[r.id] = String(r.quantity);
    setPerRes(seed);
  }, [item]);

  if (!item) return null;
  const active = (item.reservations || []).filter(
    (r) => !r.released_at && !r.received_at
  );
  const totalToReceive = Object.values(perRes).reduce(
    (s, v) => s + (Number(v) || 0),
    0
  );
  const canSubmit =
    Object.entries(perRes).every(([rid, v]) => {
      const r = active.find((x) => x.id === rid);
      if (!r) return false;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 && n <= Number(r.quantity);
    });

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="mt-receive-partial-dialog">
        <DialogHeader>
          <DialogTitle>Partial Receive</DialogTitle>
          <DialogDescription>
            Enter the actual quantity received per source shop. Damaged or missing
            units can be recorded by entering a lower value.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            {item.product_snapshot?.product_name} ·{" "}
            <span className="font-medium">{item.destination_shop_name}</span>
          </div>
          <div className="border rounded divide-y">
            {active.map((r) => (
              <div key={r.id} className="p-2 flex items-center gap-2 text-sm">
                <div className="flex-1">
                  <div className="font-medium">{r.source_shop_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    reserved <span className="font-mono">{r.quantity}</span>
                  </div>
                </div>
                <Input
                  type="number"
                  min="0"
                  max={r.quantity}
                  value={perRes[r.id] ?? ""}
                  onChange={(e) =>
                    setPerRes((prev) => ({ ...prev, [r.id]: e.target.value }))
                  }
                  className="h-8 w-20 text-right font-mono"
                  data-testid={`mt-receive-qty-${r.id}`}
                />
              </div>
            ))}
          </div>
          <div className="text-xs text-right">
            Total to receive:{" "}
            <span className="font-mono font-semibold">{totalToReceive}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="mt-receive-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            className="text-white"
            style={{ backgroundColor: "#10b981" }}
            onClick={() =>
              onConfirm(
                Object.entries(perRes).map(([rid, v]) => ({
                  reservation_id: rid,
                  quantity_received: Number(v) || 0,
                }))
              )
            }
            data-testid="mt-receive-confirm"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Confirm Receive ({totalToReceive})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
